<template>
  <TransitionGroup
    enter-from-class="opacity-0 scale-105"
    enter-active-class="transition-all duration-500 w-full h-screen overflow-hidden ease-in-out"
    leave-to-class="opacity-0"
    @before-enter="addOverflow"
    @after-enter="removeOverflow">
    <div v-if="httpError && !hub" class="min-h-screen w-full flex items-center justify-center p-6">
      <NtPageHeader :heading="`Error ${httpError.code}`" :introduction="httpError.message" />
    </div>
    <RouterView v-else-if="isLoggingIn && hub" />
    <NtConfirm
      v-else-if="authOffices && authOffices.length === 0"
      level="danger"
      icon="fa-exclamation-triangle"
      heading="Forbidden"
      confirm-label="Log Out"
      @confirmed="logOut"
      no-dismiss
      hide-cancel
      auto-open>
      You do not have access to this system.
    </NtConfirm>
    <div v-else-if="isInitialized">
      <NtNavigationSidebarContainer ref="sidebar" />
      <div class="lg:pl-72">
        <NtHeaderBar @open-sidebar="$refs.sidebar.open()" />
        <main class="py-10 min-h-screen">
          <div class="px-4 sm:px-6 lg:px-8">
            <div class="container @container mx-auto">
              <NtPageHeader
                v-if="httpError?.code > 400"
                :heading="httpError?.code === 9999 ? 'Something Went Wrong..' : `Error ${httpError.code}`"
                :introduction="httpError.message" />
              <RouterView v-else />

              <NtConfirm
                v-if="httpError?.code === 400"
                level="danger"
                icon="fa-exclamation-triangle"
                heading="Error"
                confirm-label="OK"
                hide-cancel
                auto-open
                @confirmed="clearHttpError">
                {{ httpError.message }}
              </NtConfirm>
            </div>
          </div>
        </main>

        <footer class="px-4 sm:px-6 lg:px-8 mb-10 text-center text-xs text-neutral-400">
          {{ hub.name }} provided by
          <a href="https://nurtur.tech" target="_blank" class="underline hover:text-accent-500">nurtur.tech</a>
        </footer>
      </div>
      <NtUploadProgress />
      <NtNotificationToast />
      <NtMembersHubAlert :alert="displayedAlert" @closed="dismissAlert" auto-open />
    </div>

    <div v-else class="flex flex-col h-screen items-center justify-center p-6">
      <NtLoader class="mb-3" loading />
    </div>
  </TransitionGroup>
</template>

<script>
import { RouterView } from 'vue-router'
import { mapActions, mapState } from 'pinia'
import { useAuthStore } from './stores/auth'
import { useErrorStore } from './stores/error'
import NtLoader from './components/general/NtLoader.vue'
import NtNavigationSidebarContainer from './components/navigation/NtNavigationSidebarContainer.vue'
import PageView from './views/PageView.vue'
import { useSystemStore } from './stores/system'
import NtHeaderBar from './components/navigation/NtHeaderBar.vue'
import NtPageHeader from './components/general/NtPageHeader.vue'
import NtUploadProgress from './components/general/NtUploadProgress.vue'
import NtConfirm from './components/general/NtConfirm.vue'
import NtNotificationToast from './components/navigation/NtNotificationToast.vue'
import NtMembersHubAlert from './components/partials/dashboard/NtMembersHubAlert.vue'
import { useNotificationStore } from './stores/notifications'

export default {
  components: {
    RouterView,
    NtLoader,
    NtNavigationSidebarContainer,
    NtHeaderBar,
    NtPageHeader,
    NtUploadProgress,
    NtConfirm,
    NtNotificationToast,
    NtMembersHubAlert
  },
  computed: {
    ...mapState(useSystemStore, ['systemInitialized', 'hub', 'routes']),
    ...mapState(useAuthStore, ['authInitialized', 'authOffices']),
    ...mapState(useErrorStore, ['httpError']),
    ...mapState(useNotificationStore, ['displayedAlert']),
    isLoggingIn() {
      return this.$route.path === '/oauth/login'
    },
    isInitialized() {
      return this.systemInitialized && this.authInitialized
    }
  },
  async created() {
    await this.$router.isReady()
    try {
      await this.initSystem()
      if (!this.isLoggingIn) {
        await this.initAuth()
      }
    } catch (e) {
      console.error('Failed to initialize members hub.', e.message)
    }
  },
  methods: {
    ...mapActions(useAuthStore, ['initAuth', 'logOut']),
    ...mapActions(useSystemStore, ['initSystem', 'rebuildRoutes']),
    ...mapActions(useNotificationStore, ['dismissAlert']),
    ...mapActions(useErrorStore, ['clearHttpError']),
    addOverflow() {
      document.body.classList.add('overflow-hidden')
    },
    removeOverflow() {
      document.body.classList.remove('overflow-hidden')
    }
  },
  watch: {
    routes: {
      handler() {
        this.rebuildRoutes(this.$router, PageView)
      },
      deep: true
    }
  }
}
</script>
